import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { forms as formsApi, brands as brandsApi } from '@/utils/api';
import { Plus, Trash2, X, Copy, ExternalLink, GripVertical, Eye, Save } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Radio' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date' },
  { value: 'url', label: 'URL' },
];

const makeId = () => Math.random().toString(36).slice(2, 10);

function SortableFieldRow({ field, allFields, onChange, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: field.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const [expanded, setExpanded] = useState(false);
  return (
    <div ref={setNodeRef} style={style} className="card-base p-3 mb-2" data-testid={`field-row-${field.id}`}>
      <div className="flex items-start gap-2">
        <button {...attributes} {...listeners} className="cursor-grab touch-none p-1 mt-1" style={{ color: 'var(--muted)' }}>
          <GripVertical size={14} />
        </button>
        <div className="flex-1">
          <div className="grid grid-cols-12 gap-2 items-center">
            <input className="input-agency text-sm col-span-5" placeholder="Field label" value={field.label}
                   onChange={e => onChange({ ...field, label: e.target.value })} data-testid={`field-label-${field.id}`} />
            <select className="input-agency text-sm col-span-3" value={field.type}
                    onChange={e => onChange({ ...field, type: e.target.value })}>
              {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <label className="col-span-2 flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted)' }}>
              <input type="checkbox" checked={!!field.required}
                     onChange={e => onChange({ ...field, required: e.target.checked })} />
              Required
            </label>
            <button type="button" className="col-span-1 text-xs" style={{ color: 'var(--muted)' }}
                    onClick={() => setExpanded(e => !e)}>{expanded ? 'Less' : 'More'}</button>
            <button type="button" onClick={onDelete} className="col-span-1 p-1" style={{ color: '#B34A3A' }} data-testid={`delete-field-${field.id}`}>
              <Trash2 size={14} />
            </button>
          </div>
          {expanded && (
            <div className="mt-3 space-y-2">
              <input className="input-agency text-sm" placeholder="Placeholder / help text"
                     value={field.placeholder || ''}
                     onChange={e => onChange({ ...field, placeholder: e.target.value })} />
              {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
                <input className="input-agency text-sm" placeholder="Options (comma-separated)"
                       value={(field.options || []).join(', ')}
                       onChange={e => onChange({ ...field, options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
              )}
              {/* Conditional logic */}
              <div className="p-2 rounded-btn" style={{ background: 'var(--bg)' }}>
                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--muted)' }}>Conditional logic (show only when)</p>
                <div className="grid grid-cols-3 gap-2">
                  <select className="input-agency text-xs" value={field.conditional?.field_id || ''}
                          onChange={e => onChange({ ...field, conditional: { ...(field.conditional || {}), field_id: e.target.value } })}>
                    <option value="">(none)</option>
                    {allFields.filter(f => f.id !== field.id).map(f => <option key={f.id} value={f.id}>{f.label || '(unnamed)'}</option>)}
                  </select>
                  <select className="input-agency text-xs" value={field.conditional?.op || 'equals'}
                          onChange={e => onChange({ ...field, conditional: { ...(field.conditional || {}), op: e.target.value } })}>
                    <option value="equals">equals</option>
                    <option value="not_equals">not equals</option>
                    <option value="contains">contains</option>
                    <option value="truthy">is set</option>
                  </select>
                  <input className="input-agency text-xs" placeholder="value" value={field.conditional?.value || ''}
                         onChange={e => onChange({ ...field, conditional: { ...(field.conditional || {}), value: e.target.value } })} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FormEditor({ form, onChange }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    brandsApi.list().then(r => setBrands(r.data)).catch(() => {});
  }, []);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = form.fields.findIndex(f => f.id === active.id);
    const newIndex = form.fields.findIndex(f => f.id === over.id);
    onChange({ ...form, fields: arrayMove(form.fields, oldIndex, newIndex) });
  };

  const addField = () => {
    onChange({
      ...form,
      fields: [...(form.fields || []), { id: makeId(), type: 'text', label: '', required: false }],
    });
  };

  const updateField = (idx, newField) => {
    const fields = [...form.fields];
    fields[idx] = newField;
    onChange({ ...form, fields });
  };

  const removeField = (idx) => {
    onChange({ ...form, fields: form.fields.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Title</label>
          <input className="input-agency text-sm" value={form.title} onChange={e => onChange({ ...form, title: e.target.value })} data-testid="form-title-input" />
        </div>
        <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Brand</label>
          <select className="input-agency text-sm" value={form.brand_id || ''} onChange={e => onChange({ ...form, brand_id: e.target.value })}>
            <option value="">Choose brand</option>
            {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Slug (URL)</label>
          <input className="input-agency text-sm font-mono" value={form.slug}
                 onChange={e => onChange({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                 data-testid="form-slug-input" />
        </div>
        <div className="flex items-end gap-2">
          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
            <input type="checkbox" checked={!!form.is_published} onChange={e => onChange({ ...form, is_published: e.target.checked })} data-testid="form-published-toggle" />
            Published (publicly accessible)
          </label>
        </div>
      </div>
      <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Description</label>
        <textarea className="input-agency text-sm resize-none" rows={2} value={form.description || ''} onChange={e => onChange({ ...form, description: e.target.value })} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Fields — drag to reorder</label>
          <button onClick={addField} className="text-xs flex items-center gap-1" style={{ color: '#006080' }} data-testid="add-field-button">
            <Plus size={12} /> Add Field
          </button>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={(form.fields || []).map(f => f.id)} strategy={verticalListSortingStrategy}>
            {(form.fields || []).map((field, idx) => (
              <SortableFieldRow key={field.id} field={field} allFields={form.fields}
                                onChange={(nf) => updateField(idx, nf)}
                                onDelete={() => removeField(idx)} />
            ))}
          </SortableContext>
        </DndContext>
        {(form.fields || []).length === 0 && (
          <div className="card-base p-6 text-center">
            <p className="text-sm" style={{ color: 'var(--muted)' }}>No fields yet. Click "Add Field" to begin.</p>
          </div>
        )}
      </div>

      <div>
        <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Thank-you message</label>
        <input className="input-agency text-sm" value={form.settings?.thank_you_message || ''}
               onChange={e => onChange({ ...form, settings: { ...(form.settings || {}), thank_you_message: e.target.value } })}
               placeholder="Thanks — we'll be in touch shortly." />
      </div>
    </div>
  );
}

export default function Forms() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submissions, setSubmissions] = useState([]);

  const load = () => formsApi.list().then(r => setForms(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (formId && formId !== 'new') {
      formsApi.get(formId).then(r => setSelected(r.data));
      formsApi.submissions(formId).then(r => setSubmissions(r.data)).catch(() => {});
    } else if (formId === 'new') {
      setSelected({ title: 'New Intake', slug: `intake-${Date.now()}`, fields: [], is_published: false, settings: {} });
    } else {
      setSelected(null);
    }
  }, [formId]);

  const handleSave = async () => {
    if (!selected?.title || !selected?.slug || !selected?.brand_id) {
      toast.error('Title, slug, and brand are required');
      return;
    }
    setSaving(true);
    try {
      if (selected.id) {
        const r = await formsApi.update(selected.id, selected);
        setSelected(r.data);
        toast.success('Form saved');
      } else {
        const r = await formsApi.create(selected);
        toast.success('Form created');
        navigate(`/admin/forms/${r.data.id}`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected?.id || !window.confirm('Delete this form?')) return;
    await formsApi.delete(selected.id);
    navigate('/admin/forms');
    load();
  };

  const publicUrl = selected?.slug ? `${window.location.origin}/intake/${selected.slug}` : '';

  // List view
  if (!selected) {
    return (
      <div className="p-6 animate-fade-in" data-testid="forms-page">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-black" style={{ color: 'var(--ink)' }}>Intake Forms</h1>
          <button className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5" onClick={() => navigate('/admin/forms/new')} data-testid="create-form-button">
            <Plus size={13} /> New Form
          </button>
        </div>
        {forms.length === 0 ? (
          <div className="card-base p-10 text-center">
            <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>No forms yet</p>
            <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Build a public intake form so prospects can self-serve.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {forms.map(f => (
              <div key={f.id} className="card-interactive p-4 flex items-center justify-between" onClick={() => navigate(`/admin/forms/${f.id}`)} data-testid={`form-item-${f.id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{f.title}</p>
                    {f.is_published ? (
                      <span className="badge-status" style={{ background: '#3C8A5A15', color: '#3C8A5A' }}>Published</span>
                    ) : (
                      <span className="badge-status" style={{ background: '#E8E3D9', color: '#5C6B72' }}>Draft</span>
                    )}
                  </div>
                  <p className="text-xs mt-0.5 font-mono" style={{ color: 'var(--muted)' }}>/intake/{f.slug}</p>
                </div>
                <div className="text-xs" style={{ color: 'var(--muted)' }}>
                  {f.submission_count || 0} submissions
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Editor view
  return (
    <div className="p-6 animate-fade-in" data-testid="form-editor-page">
      <button className="text-xs mb-2 flex items-center gap-1" style={{ color: 'var(--muted)' }} onClick={() => navigate('/admin/forms')}>
        ← Back to forms
      </button>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-black" style={{ color: 'var(--ink)' }}>{selected.id ? 'Edit Form' : 'New Form'}</h1>
        <div className="flex gap-2">
          {selected.is_published && selected.id && (
            <a className="btn-secondary text-xs flex items-center gap-1.5 px-3 py-1.5" href={publicUrl} target="_blank" rel="noreferrer">
              <ExternalLink size={13} /> Open public URL
            </a>
          )}
          {selected.id && (
            <button className="text-xs px-3 py-1.5 rounded-btn flex items-center gap-1" style={{ color: '#B34A3A', border: '1px solid #B34A3A' }} onClick={handleDelete} data-testid="delete-form-button">
              <Trash2 size={12} /> Delete
            </button>
          )}
          <button className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5" disabled={saving} onClick={handleSave} data-testid="save-form-button">
            <Save size={13} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {selected.id && publicUrl && (
        <div className="card-base p-3 mb-4 flex items-center gap-3">
          <div className="flex-1 text-sm font-mono truncate" style={{ color: 'var(--muted)' }}>{publicUrl}</div>
          <button className="text-xs flex items-center gap-1" style={{ color: '#006080' }} onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('URL copied'); }}>
            <Copy size={12} /> Copy
          </button>
        </div>
      )}

      <FormEditor form={selected} onChange={setSelected} />

      {selected.id && submissions.length > 0 && (
        <div className="mt-6">
          <h2 className="font-bold text-sm mb-2" style={{ color: 'var(--ink)' }}>Recent submissions ({submissions.length})</h2>
          <div className="space-y-2">
            {submissions.slice(0, 10).map(s => (
              <div key={s.id} className="card-base p-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{s.name || s.email || 'Submission'}</p>
                  <p className="text-xs" style={{ color: 'var(--muted)' }}>{new Date(s.created_at).toLocaleString()}</p>
                </div>
                <pre className="text-xs whitespace-pre-wrap" style={{ color: 'var(--muted)' }}>{JSON.stringify(s.data, null, 2)}</pre>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
